import React from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Box,
  Typography,
} from '@mui/material';
import { useTranslation } from '../../../hooks/useTranslation';
import { LanguageOption } from '../../../types/i18n';
import { useIsFeatureEnabled } from '../../../hooks/useIsFeatureEnabled';
import { FeatureGate } from '../../../interfaces/FeatureGate';

interface LanguageSelectorProps {
  forceWhiteText?: boolean;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  forceWhiteText = false,
}) => {
  const { t, changeLanguage, currentLanguage } = useTranslation();
  const isPhilippinesLanguageEnabled = useIsFeatureEnabled(
    FeatureGate.PHILIPPINES_LANGUAGE_SUPPORT
  );

  const baseLanguageOptions: LanguageOption[] = [
    { code: 'en', name: t('English'), flag: '🇺🇸' },
    { code: 'fr', name: t('French'), flag: '🇫🇷' },
    { code: 'it', name: t('Italian'), flag: '🇮🇹' },
    { code: 'es', name: t('Spanish'), flag: '🇪🇸' },
    { code: 'de', name: t('German'), flag: '🇩🇪' },
    { code: 'nl', name: t('Dutch'), flag: '🇳🇱' },
  ];

  // Conditionally add Filipino based on feature gate
  const languageOptions: LanguageOption[] = [
    ...baseLanguageOptions,
    ...(isPhilippinesLanguageEnabled
      ? [{ code: 'fil', name: t('Filipino'), flag: '🇵🇭' }]
      : []),
  ];

  const getCurrentLanguageOption = () => {
    return (
      languageOptions.find(opt => opt.code === currentLanguage) ||
      languageOptions[0]
    );
  };

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const newLanguage = event.target.value;
    changeLanguage(newLanguage);
  };

  return (
    <FormControl
      variant="outlined"
      size="small"
      sx={{
        minWidth: 140,
        color: forceWhiteText ? '#FFFFFF' : 'inherit',
        '& .MuiOutlinedInput-root': {
          '& fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.3)',
          },
          '&:hover fieldset': {
            borderColor: 'rgba(255, 255, 255, 0.5)',
          },
          '&.Mui-focused fieldset': {
            borderColor: 'primary.main',
          },
        },
        '& .MuiSelect-select': {
          color: forceWhiteText ? '#FFFFFF' : 'inherit',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        },
        '& .MuiSvgIcon-root': {
          color: forceWhiteText ? '#FFFFFF' : 'inherit',
        },
      }}
    >
      <Select
        value={currentLanguage}
        onChange={handleLanguageChange}
        displayEmpty
        renderValue={() => {
          const option = getCurrentLanguageOption();
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>{option.flag}</span>
              <Typography
                variant="body2"
                sx={{ color: forceWhiteText ? '#FFFFFF' : 'inherit' }}
              >
                {option.name}
              </Typography>
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              backgroundColor: 'background.paper',
              boxShadow: 3,
              '& .MuiMenuItem-root': {
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              },
            },
          },
        }}
      >
        {languageOptions.map(option => (
          <MenuItem key={option.code} value={option.code}>
            <span>{option.flag}</span>
            <Typography variant="body2">{option.name}</Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default LanguageSelector;
